/**
 * KnobsEditModal.tsx — pick what gets pinned in the sidebar's Knobs section.
 *
 * Two tabs in the header (no title — the tabs ARE the header, mirroring
 * AgentModal): Graph + Database.
 *
 *  - Graph: search + toggle pin on any node/edge param. Range + label resolve
 *    live from the underlying ParamSpec (the pin just stores the path).
 *  - Database: only when a backend is wired (tablesState). Create a db knob by
 *    picking table → row → numeric column → range; manage existing db pins.
 *
 * Both tabs edit a shared draft (the whole KnobPin[] list) and commit through
 * `onChange` on every add/remove — immediate-apply, no Apply button, matching
 * the codebase's modal convention.
 */
import { useEffect, useMemo, useState } from 'react';
import { Check, Plus, MagnifyingGlass, X } from '@phosphor-icons/react';

import type { DbKnobPin, GraphKnobPin, KnobPin, ParamSpec, ScenarioBundle } from '../engine/types';
import { uuid } from '../library/ids';
import { Modal } from './Modal';
import type { KnobTableSnapshot } from './Sidebar';

interface KnobsEditModalProps {
  bundle: ScenarioBundle;
  knobs: KnobPin[];
  /** Live param values keyed by setParam path (label fallback for un-pinned
   *  params with no label override). */
  paramValues?: Record<string, number>;
  /** Project tables — null in local mode (the Database tab is hidden) and
   *  before the first load lands. */
  tablesState: KnobTableSnapshot[] | null;
  /** Lazy-load the tables snapshot. Fired when the Database tab opens. */
  onLoadTables?: () => Promise<void>;
  onClose: () => void;
  /** Commit the whole knobs list. Called on every add/remove/edit. */
  onChange: (knobs: KnobPin[]) => void;
}

type Tab = 'graph' | 'db';

export function KnobsEditModal({
  bundle,
  knobs,
  paramValues: _paramValues,
  tablesState,
  onLoadTables,
  onClose,
  onChange,
}: KnobsEditModalProps): JSX.Element {
  const canUseDb = !!onLoadTables || tablesState !== null;
  const [tab, setTab] = useState<Tab>(canUseDb ? 'graph' : 'graph');

  // The Database tab needs tables loaded. Pull them once when the user opens
  // that tab (mirrors the TablesPanel's lazy load).
  useEffect(() => {
    if (tab === 'db' && tablesState === null && onLoadTables) {
      void onLoadTables();
    }
  }, [tab, tablesState, onLoadTables]);

  const tabs = (
    <div className="agent-tabs agent-tabs-header" role="tablist">
      <button
        type="button"
        role="tab"
        aria-selected={tab === 'graph'}
        className={`agent-tab${tab === 'graph' ? ' on' : ''}`}
        onClick={() => setTab('graph')}
      >Graph</button>
      {canUseDb && (
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'db'}
          className={`agent-tab${tab === 'db' ? ' on' : ''}`}
          onClick={() => setTab('db')}
        >Database</button>
      )}
    </div>
  );

  return (
    <Modal headerLeft={tabs} onClose={onClose} size="wide">
      {tab === 'graph' ? (
        <GraphKnobsTab bundle={bundle} knobs={knobs} onChange={onChange} />
      ) : (
        <DbKnobsTab knobs={knobs} tablesState={tablesState} onChange={onChange} />
      )}
    </Modal>
  );
}

// ─── Graph tab ──────────────────────────────────────────────────────────────

/** One pinnable graph param, flattened with enough context to render. */
interface GraphParamRow {
  path: string;
  spec: ParamSpec;
  ownerLabel: string;
  ownerKind: 'node' | 'edge';
}

/** Walk the bundle and collect every pinnable param (node params, gate params,
 *  source distParams, edge params) with its setParam path + owner label. */
function collectGraphParams(bundle: ScenarioBundle): GraphParamRow[] {
  const rows: GraphParamRow[] = [];
  for (const node of bundle.graph.nodes) {
    for (const p of node.params) {
      rows.push({ path: `${node.id}.${p.key}`, spec: p, ownerLabel: node.label, ownerKind: 'node' });
    }
    if (node.gate?.params) {
      for (const p of node.gate.params) {
        rows.push({ path: `${node.id}.${p.key}`, spec: p, ownerLabel: node.label, ownerKind: 'node' });
      }
    }
    if (node.source?.distParams) {
      for (const p of node.source.distParams) {
        rows.push({ path: `${node.id}.${p.key}`, spec: p, ownerLabel: node.label, ownerKind: 'node' });
      }
    }
  }
  for (const edge of bundle.graph.edges) {
    for (const p of edge.params) {
      const src = bundle.graph.nodes.find((n) => n.id === edge.source)?.label ?? edge.source;
      const tgt = bundle.graph.nodes.find((n) => n.id === edge.target)?.label ?? edge.target;
      rows.push({ path: `e:${edge.id}.${p.key}`, spec: p, ownerLabel: `${src} → ${tgt}`, ownerKind: 'edge' });
    }
  }
  return rows;
}

function GraphKnobsTab({
  bundle,
  knobs,
  onChange,
}: {
  bundle: ScenarioBundle;
  knobs: KnobPin[];
  onChange: (knobs: KnobPin[]) => void;
}): JSX.Element {
  const allParams = useMemo(() => collectGraphParams(bundle), [bundle]);
  const [query, setQuery] = useState('');

  const pinnedPaths = useMemo(
    () => new Set(knobs.filter((k): k is GraphKnobPin => k.type === 'graph').map((k) => k.path)),
    [knobs],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allParams;
    return allParams.filter((r) =>
      r.spec.label.toLowerCase().includes(q) ||
      r.spec.key.toLowerCase().includes(q) ||
      r.ownerLabel.toLowerCase().includes(q),
    );
  }, [allParams, query]);

  const toggle = (row: GraphParamRow) => {
    const graphPins = knobs.filter((k): k is GraphKnobPin => k.type === 'graph');
    const dbPins = knobs.filter((k): k is DbKnobPin => k.type === 'db');
    if (pinnedPaths.has(row.path)) {
      // Unpin — keep order of the remaining graph pins, then db pins.
      onChange([...graphPins.filter((k) => k.path !== row.path), ...dbPins]);
    } else {
      const newPin: GraphKnobPin = { id: uuid(), type: 'graph', path: row.path };
      onChange([...graphPins, newPin, ...dbPins]);
    }
  };

  return (
    <div className="knob-graph-tab">
      <div className="search-field">
        <MagnifyingGlass size={14} className="search-field-icon" />
        <input
          className="text-input search-field-input"
          placeholder="Search knobs by name, key, or node…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
      </div>
      {filtered.length === 0 ? (
        <p className="knob-empty">
          {allParams.length === 0
            ? 'No knobs in this graph. Add params to a node or edge first.'
            : 'No matches.'}
        </p>
      ) : (
        <ul className="knob-search-list">
          {filtered.map((row) => {
            const pinned = pinnedPaths.has(row.path);
            return (
              <li key={row.path}>
                <button
                  type="button"
                  className={`knob-search-row${pinned ? ' pinned' : ''}`}
                  onClick={() => toggle(row)}
                  title={pinned ? 'Unpin' : 'Pin to sidebar'}
                >
                  <span className="knob-search-main">
                    <span className="knob-search-label">{row.spec.label}</span>
                    <span className="knob-search-sub">
                      <code>{row.spec.key}</code>
                      <span className="knob-search-owner">{row.ownerLabel}</span>
                    </span>
                  </span>
                  <span className={`knob-pin-toggle${pinned ? ' on' : ''}`}>
                    {pinned ? <Check size={14} /> : <Plus size={14} />}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ─── Database tab ───────────────────────────────────────────────────────────

const NUMERIC_TYPES = new Set(['integer', 'float', 'real', 'numeric', 'int']);
function isNumeric(type: string): boolean {
  return NUMERIC_TYPES.has(type.toLowerCase());
}

function DbKnobsTab({
  knobs,
  tablesState,
  onChange,
}: {
  knobs: KnobPin[];
  tablesState: KnobTableSnapshot[] | null;
  onChange: (knobs: KnobPin[]) => void;
}): JSX.Element {
  const dbPins = knobs.filter((k): k is DbKnobPin => k.type === 'db');

  const remove = (id: string) => onChange(knobs.filter((k) => k.id !== id));
  const update = (id: string, patch: Partial<DbKnobPin>) =>
    onChange(knobs.map((k) => (k.id === id && k.type === 'db' ? { ...k, ...patch } : k)));

  if (tablesState === null) {
    return (
      <div className="knob-db-tab">
        <p className="knob-empty">Loading project tables…</p>
      </div>
    );
  }
  if (tablesState.length === 0) {
    return (
      <div className="knob-db-tab">
        <p className="knob-empty">This project has no tables. Create one in the Tables panel first.</p>
      </div>
    );
  }

  return (
    <div className="knob-db-tab">
      <DbKnobCreateForm tablesState={tablesState} onAdd={(pin) => onChange([...knobs, pin])} />
      {dbPins.length > 0 && (
        <>
          <div className="knob-db-section-title">Pinned database knobs</div>
          <ul className="knob-db-list">
            {dbPins.map((pin) => (
              <li key={pin.id} className="knob-db-row">
                <input
                  className="text-input knob-db-row-label"
                  value={pin.label}
                  onChange={(e) => update(pin.id, { label: e.target.value })}
                />
                <span className="knob-db-row-loc">{pin.table}.{pin.column} #{pin.rowId}</span>
                <div className="knob-db-row-range">
                  <input
                    type="number"
                    className="text-input"
                    value={pin.min}
                    aria-label="min"
                    onChange={(e) => update(pin.id, { min: Number(e.target.value) })}
                  />
                  <input
                    type="number"
                    className="text-input"
                    value={pin.max}
                    aria-label="max"
                    onChange={(e) => update(pin.id, { max: Number(e.target.value) })}
                  />
                  <input
                    type="number"
                    className="text-input"
                    value={pin.step ?? ''}
                    aria-label="step"
                    placeholder="step"
                    onChange={(e) => {
                      const v = e.target.value;
                      update(pin.id, { step: v === '' ? undefined : Number(v) });
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="btn-icon-mini"
                  onClick={() => remove(pin.id)}
                  title="Unpin"
                >
                  <X size={12} />
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function DbKnobCreateForm({
  tablesState,
  onAdd,
}: {
  tablesState: KnobTableSnapshot[];
  onAdd: (pin: DbKnobPin) => void;
}): JSX.Element {
  const [table, setTable] = useState<string>(tablesState[0]?.name ?? '');
  const [rowQuery, setRowQuery] = useState('');
  const [rowId, setRowId] = useState<number | string | null>(null);
  const [column, setColumn] = useState<string>('');
  const [label, setLabel] = useState('');
  const [min, setMin] = useState(0);
  const [max, setMax] = useState(100);
  const [step, setStep] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);

  const selectedTable = tablesState.find((t) => t.name === table) ?? null;
  const numericColumns = useMemo(
    () => (selectedTable?.columns ?? []).filter((c) => isNumeric(c.type)),
    [selectedTable],
  );

  // Reset downstream selections when the table changes.
  useEffect(() => {
    setRowId(null);
    setColumn('');
    setRowQuery('');
  }, [table]);

  // When a row + column are picked, seed the label + a sensible range from the
  // current cell value (max = value×2 or 100 so the slider starts mid-range).
  useEffect(() => {
    if (rowId == null || !column || !selectedTable) return;
    const row = selectedTable.rows.find((r) => String(r.id) === String(rowId));
    const v = row ? row.cells[column] : null;
    if (typeof v === 'number') {
      setMax(v <= 0 ? 100 : Math.max(100, Math.ceil(v * 2)));
      if (min >= v) setMin(0);
    }
    if (!label) setLabel(`${table}.${column} #${rowId}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowId, column]);

  const rowLabel = (r: { id: number | string; cells: Record<string, string | number | boolean | null> }): string => {
    const firstText = Object.entries(r.cells).find(([, v]) => typeof v === 'string')?.[1];
    return `#${r.id}${firstText ? ` · ${firstText}` : ''}`;
  };

  const filteredRows = useMemo(() => {
    if (!selectedTable) return [];
    const q = rowQuery.trim().toLowerCase();
    const rows = q
      ? selectedTable.rows.filter((r) =>
          rowLabel(r).toLowerCase().includes(q) ||
          String(r.id) === q,
        )
      : selectedTable.rows;
    return rows.slice(0, 50);
  }, [selectedTable, rowQuery]);

  const canAdd = table && rowId != null && !!column;

  const submit = () => {
    if (!canAdd) {
      setError('Pick a table, row, and column.');
      return;
    }
    if (max <= min) {
      setError('Max must be greater than min.');
      return;
    }
    setError(null);
    const pin: DbKnobPin = {
      id: uuid(),
      type: 'db',
      label: label.trim() || `${table}.${column} #${rowId}`,
      table,
      rowId: rowId!,
      column,
      min,
      max,
      step: step === '' ? undefined : step,
    };
    onAdd(pin);
    // Reset for the next pin (keep the table — usually you pin a few in a row).
    setRowId(null);
    setColumn('');
    setLabel('');
    setMax(100);
    setMin(0);
    setStep('');
  };

  return (
    <div className="knob-db-form">
      <div className="knob-db-section-title">Add a database knob</div>
      <div className="knob-db-grid">
        <label className="knob-field">
          <span>Table</span>
          <select className="text-input" value={table} onChange={(e) => setTable(e.target.value)}>
            {tablesState.filter(t => !t.name.includes('chat_')).map((t) => (
              <option key={t.name} value={t.name}>{t.name}</option>
            ))}
          </select>
        </label>

        <label className="knob-field">
          <span>Column <em className="knob-field-hint">(numeric only)</em></span>
          <select
            className="text-input"
            value={column}
            onChange={(e) => setColumn(e.target.value)}
            disabled={!selectedTable}
          >
            <option value="">— select —</option>
            {numericColumns.map((c) => (
              <option key={c.name} value={c.name}>{c.name} ({c.type})</option>
            ))}
          </select>
          {selectedTable && numericColumns.length === 0 && (
            <em className="knob-field-hint">This table has no numeric columns.</em>
          )}
        </label>

        <label className="knob-field knob-field-wide">
          <span>Search row</span>
          <input
            className="text-input"
            placeholder="Filter by id or text…"
            value={rowQuery}
            onChange={(e) => setRowQuery(e.target.value)}
            disabled={!selectedTable}
          />
        </label>

        <div className="knob-field knob-field-wide knob-row-picker">
          <span>Select a row</span>
          <ul className="knob-row-list">
            {filteredRows.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  className={`knob-row-pick${rowId != null && String(rowId) === String(r.id) ? ' on' : ''}`}
                  onClick={() => setRowId(r.id)}
                >
                  {rowLabel(r)}
                </button>
              </li>
            ))}
            {filteredRows.length === 0 && <li className="knob-empty">No rows.</li>}
          </ul>
        </div>

        <label className="knob-field knob-field-wide">
          <span>Label</span>
          <input
            className="text-input"
            value={label}
            placeholder={column && rowId != null ? `${table}.${column} #${rowId}` : ''}
            onChange={(e) => setLabel(e.target.value)}
          />
        </label>

        <label className="knob-field">
          <span>Min</span>
          <input type="number" className="text-input" value={min} onChange={(e) => setMin(Number(e.target.value))} />
        </label>
        <label className="knob-field">
          <span>Max</span>
          <input type="number" className="text-input" value={max} onChange={(e) => setMax(Number(e.target.value))} />
        </label>
        <label className="knob-field">
          <span>Step</span>
          <input
            type="number"
            className="text-input"
            value={step}
            placeholder="auto"
            onChange={(e) => setStep(e.target.value === '' ? '' : Number(e.target.value))}
          />
        </label>
      </div>

      {error && <p className="knob-error">{error}</p>}

      <button
        type="button"
        className="btn btn-primary knob-db-add"
        onClick={submit}
        disabled={!canAdd}
      >
        <Plus size={14} style={{ marginRight: 6 }} /> Add knob
      </button>
    </div>
  );
}
